import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { Button } from '../components/ui/button';
import {
  Leaf, Recycle, Wallet, Hammer, Globe, ArrowRight, TreePine, TrendingDown, Heart,
} from 'lucide-react';

// Long-form SEO page making the case that learning the sellier trade is
// an act of environmental and economical preservation. Heavy hand-written
// content with strong H2 structure for Google.
export const ImpactEnvironnementalPage = () => {
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);

  useEffect(() => {
    document.title = 'Sellerie & environnement — réemploi, réparation, anti-gaspillage';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        "La sellerie-garnissage est un métier de réparation et de réemploi. Découvrez son impact concret sur l'environnement, la réduction des déchets et les économies pour le consommateur."
      );
    }
  }, []);

  // JSON-LD Article schema
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Sellerie-garnissage et environnement : le métier qui répare au lieu de jeter',
    author: { '@type': 'Organization', name: 'Sellerie Académie' },
    publisher: { '@type': 'Organization', name: 'Sellerie Académie' },
    mainEntityOfPage: typeof window !== 'undefined' ? window.location.href : '',
    description:
      "La sellerie-garnissage est un métier de réparation et de réemploi qui réduit massivement les déchets, prolonge la durée de vie des objets et permet aux particuliers comme aux entreprises de réduire leurs coûts.",
  };

  const PILLARS = [
    {
      Icon: Recycle,
      title: 'Réemploi',
      kicker: 'CIRCULAIRE',
      text:
        "Un siège refait dure 20 ans de plus. Un canapé regarnis évite l'achat d'un meuble neuf. Chaque pièce remise en état, c'est une pièce qui n'est pas jetée — et qui n'a pas besoin d'être produite.",
    },
    {
      Icon: Hammer,
      title: 'Réparation',
      kicker: 'LONGÉVITÉ',
      text:
        "La sellerie répare ce que l'industrie a abandonné : un cuir qui craque, une mousse qui s'effondre, une couture qui lâche. C'est l'antithèse de l'obsolescence programmée.",
    },
    {
      Icon: TrendingDown,
      title: 'Anti-gaspillage',
      kicker: 'DURABLE',
      text:
        "En France, on jette chaque année 670 000 tonnes de meubles et plusieurs millions de sièges automobile. Un sellier peut sauver la majorité de cette matière, simplement avec ses mains.",
    },
    {
      Icon: Wallet,
      title: 'Économique',
      kicker: 'RENTABLE',
      text:
        "Refaire une banquette de 4 sièges coûte 2 à 3 fois moins cher que d'acheter du neuf équivalent. Côté pro, c'est un service à forte valeur ajoutée et à faibles coûts matière.",
    },
    {
      Icon: TreePine,
      title: 'Faible empreinte',
      kicker: 'CARBONE',
      text:
        "Atelier sans procédés industriels lourds. Pas d'extraction massive, pas de fonte de plastique, pas de transport intercontinental. Outils d'aiguille, fil, agrafes, mousses, cuirs — voilà tout.",
    },
    {
      Icon: Heart,
      title: 'Patrimoine',
      kicker: 'TRANSMISSION',
      text:
        "Un véhicule de collection, un canapé de famille, un fauteuil signé — la sellerie préserve la valeur sentimentale et patrimoniale d'objets qu'on ne saurait racheter à neuf.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="impact-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero */}
      <section className="relative bg-[#1F2A4A] text-white pt-32 pb-16 lg:pt-44 lg:pb-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #C9B896 0, transparent 40%), radial-gradient(circle at 80% 70%, #C9B896 0, transparent 35%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-mono uppercase tracking-[0.35em] text-[#C9B896] mb-4">
            <Leaf className="inline w-3 h-3 mr-1.5" />
            IMPACT ENVIRONNEMENTAL · 2026
          </p>
          <h1 className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl text-[#FAF7F0]">
            La sellerie,{' '}
            <span className="italic text-[#C9B896]">un métier qui répare<br className="hidden md:inline" /> au lieu de jeter.</span>
          </h1>
          <p className="mt-7 text-base md:text-xl text-white/80 max-w-2xl font-light leading-relaxed">
            Apprendre la sellerie-garnissage, c'est entrer dans un métier qui s'inscrit naturellement
            dans la transition écologique&nbsp;: <strong className="text-white">réemploi, réparation, longévité, sobriété matière</strong>.
            Voici son impact concret.
          </p>
        </div>
      </section>

      {/* Pillars grid */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map(({ Icon, title, kicker, text }) => (
            <div
              key={title}
              className="p-6 rounded-2xl bg-white border border-[#1F2A4A]/10 hover:border-[#C9B896]/50 transition-colors"
              data-testid={`impact-pillar-${title.toLowerCase()}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#C9B896]/15 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#C9B896]" />
              </div>
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C9B896] mb-1">{kicker}</p>
              <h3 className="font-serif text-2xl text-[#1F2A4A] font-light mb-2">{title}</h3>
              <p className="text-sm text-[#1F2A4A]/75 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Numbers */}
      <section className="bg-[#0F1623] text-white py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-3">QUELQUES CHIFFRES</p>
          <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight max-w-3xl">
            Ce que la sellerie évite{' '}
            <span className="italic text-[#C9B896]">à la planète.</span>
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { n: '670 000 t', label: 'de meubles jetés chaque année en France', src: 'Ademe' },
              { n: '20 ans', label: 'de durée de vie supplémentaire pour un siège regarni', src: 'Atelier' },
              { n: '2-3 ×', label: 'moins cher que d\'acheter du neuf équivalent', src: 'Étude marché' },
              { n: '90 %', label: 'de matière conservée lors d\'une réfection', src: 'Pratique sellier' },
            ].map((s, i) => (
              <div key={i} className="border-l-2 border-[#C9B896]/40 pl-5">
                <p className="font-serif text-5xl md:text-6xl text-[#C9B896] font-light tracking-tight leading-none">{s.n}</p>
                <p className="text-sm text-white/75 leading-snug mt-3">{s.label}</p>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mt-1.5 font-mono">SOURCE&nbsp;: {s.src}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-form prose */}
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <article className="prose prose-lg max-w-none text-[#1F2A4A] [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-light [&_h2]:text-[#1F2A4A] [&_h2]:mt-12 [&_h2]:mb-4 [&_p]:leading-relaxed [&_p]:text-[#1F2A4A]/85 [&_strong]:text-[#1F2A4A]">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896]">LECTURE LONGUE</p>

          <h2>Réemploi&nbsp;: prolonger la vie d'un objet plutôt que d'en produire un nouveau</h2>
          <p>
            La sellerie-garnissage est l'un des rares métiers où chaque intervention est un acte de
            <strong> réemploi</strong>. Un siège automobile fatigué n'a pas besoin d'être jeté&nbsp;:
            on retire la coiffe, on remplace la mousse abîmée, on recoupe un nouveau revêtement, on
            recoud, on remonte. Le squelette métallique d'origine — celui qui a coûté le plus en
            énergie à produire — est conservé.
          </p>
          <p>
            La même logique s'applique à un canapé, une banquette de moto, un fauteuil de
            cinéma, une selle de cheval ou un siège de bateau. Là où l'industrie pousse au
            remplacement, le sellier répare. <strong>Chaque pièce réutilisée est une pièce qui
            n'est pas produite</strong>&nbsp;: c'est autant de matière, d'énergie, de transport et
            d'émissions évités.
          </p>

          <h2>Réparation&nbsp;: l'inverse de l'obsolescence programmée</h2>
          <p>
            Aujourd'hui, l'essentiel des produits manufacturés sont conçus pour être remplacés
            plutôt que réparés&nbsp;: pièces collées, mousses thermo-injectées, coutures
            inaccessibles. La sellerie, à l'inverse, est un métier d'<strong>aiguille et de
            patience</strong>. Tout ce qui a été cousu peut être décousu. Tout ce qui a été agrafé
            peut être dégrafé. Tout ce qui a été monté peut être démonté, restauré et remonté.
          </p>
          <p>
            C'est aussi un métier accessible techniquement&nbsp;: en quelques jours de formation,
            on apprend à diagnostiquer une pièce, à choisir le bon matériau de remplacement, à
            recoudre proprement. À 3 mois, on tient un atelier. À 1 an, on est autonome.
          </p>

          <h2>Anti-gaspillage&nbsp;: chaque sellier sauve des tonnes de matière par an</h2>
          <p>
            Selon l'<strong>Ademe</strong>, les Français jettent 670&nbsp;000 tonnes de meubles
            par an — dont une partie significative est encore parfaitement réparable. Côté
            automobile, on estime que des millions de véhicules sont mis à la casse chaque année,
            alors que leur sellerie pourrait être refaite pour quelques centaines d'euros et
            prolonger leur usage d'une décennie.
          </p>
          <p>
            Un sellier-garnisseur indépendant moyen restaure entre 100 et 300 pièces par an. À
            l'échelle d'une carrière&nbsp;: c'est des milliers d'objets sauvés de la décharge.
          </p>

          <h2>Économies pour les particuliers et les entreprises</h2>
          <p>
            Refaire une banquette de 4 sièges en cuir véritable coûte typiquement entre 800 et
            1&nbsp;500&nbsp;€. L'acheter neuve équivalente&nbsp;? <strong>2 à 4 fois plus cher</strong>,
            et souvent impossible (pièces hors production). Pour les particuliers&nbsp;: c'est un
            avantage économique direct. Pour les pros&nbsp;: c'est un service à forte marge avec
            peu de coûts matière.
          </p>
          <p>
            Côté médical et nautique, la valeur ajoutée est encore plus claire — un fauteuil
            dentaire ou une banquette de bateau coûtent 5&nbsp;000 à 20&nbsp;000&nbsp;€ neufs,
            mais peuvent être regarnis pour quelques centaines d'euros.
          </p>

          <h2>Réduction du traitement des déchets</h2>
          <p>
            Quand on jette un canapé ou un siège auto, la matière part en grande partie à
            l'incinération. Bilan carbone&nbsp;: lourd. Recyclage&nbsp;: partiel (les mousses
            polyuréthane sont quasiment impossibles à recycler proprement).
          </p>
          <p>
            La sellerie évite cette étape entière&nbsp;: <strong>on conserve l'objet en l'état,
            on ne remplace que la couche d'usure</strong>. Moins de déchets à collecter, moins de
            transports, moins d'incinération, moins d'enfouissement.
          </p>

          <h2>Un métier d'avenir, ancré dans la transition</h2>
          <p>
            Avec la <strong>loi Anti-Gaspillage</strong> (AGEC), les <strong>bonus
            réparation</strong> et la <strong>REP ameublement</strong>, la France pousse
            activement le réemploi et la réparation. Le métier de sellier est en première ligne
            de cette transition — et la demande, longtemps en sommeil, repart fortement.
          </p>
          <p>
            C'est pour cela qu'on dit que la sellerie est <strong>un métier d'avenir</strong>&nbsp;:
            non pas parce qu'il est nouveau, mais parce que le monde redécouvre qu'il a besoin de
            gens qui réparent. Apprendre la sellerie, c'est entrer dans ce mouvement.
          </p>
        </article>

        {/* CTA */}
        <div className="mt-14 p-8 rounded-2xl bg-emerald-600 text-white text-center">
          <Globe className="w-8 h-8 mx-auto mb-4 text-emerald-100" />
          <h2 className="font-serif italic text-2xl md:text-3xl text-white mb-2">
            Envie d'apprendre à réparer&nbsp;?
          </h2>
          <p className="text-sm text-emerald-50 mb-5 max-w-md mx-auto">
            Découvrez nos formations en sellerie-garnissage — un métier d'avenir, ancré dans la transition écologique.
          </p>
          <Link to={L('/formations-sur-place')}>
            <Button className="bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-7 h-11" data-testid="impact-cta-formations">
              Voir les formations
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ImpactEnvironnementalPage;
